import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import { HeroContent } from '../../api/homepage';
import Bg from '../../images/bg/hero-bg02.jpg';
import HeroVisual from './HeroVisual';

// Direct per-element animation config — no parent stagger variants,
// no useAnimation, no useEffect. This is the most reliable pattern in
// React 18 concurrent / StrictMode environments with framer-motion v11.
const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] },
});

const Hero = () => {
    return (
        <section
            className="hero hero-style-two pos-rel pt-120 bg_img bt-hero"
            style={{ backgroundImage: `url(${Bg})` }}
            aria-label="Bogin Tech custom software development hero"
        >
            {/*
             * Orbs as CSS radial-gradient backgrounds on a single div.
             * radial-gradient paints INSIDE the element's own box, so
             * the .o-hidden overflow:hidden on the parent never hard-clips
             * them. Gradient centres at 110%/−12% create corner glows that
             * fade naturally to transparent — no hard edges, ever.
             */}
            <motion.div
                className="bt-hero__orbs"
                aria-hidden="true"
                animate={{ opacity: [0.78, 1, 0.88, 1, 0.78] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Subtle dot-grid overlay */}
            <div className="bt-hero__grid" aria-hidden="true" />

            <div className="container">
                <div className="hero_wrap pt-40">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="xb-hero">
                                <motion.div className="bt-hero__badge" {...fadeUp(0.05)}>
                                    <span className="bt-dot" aria-hidden="true" />
                                    <span className="bt-badge-rating">
                                        <i className="fas fa-star" aria-hidden="true" />
                                        {HeroContent.badge}
                                    </span>
                                </motion.div>

                                <motion.h1 className="xb-item--title bt-hero__title" {...fadeUp(0.18)}>
                                    {HeroContent.title.line1}<br />
                                    That Scales Your <span className="bt-accent">Business</span>
                                </motion.h1>

                                <motion.p className="xb-item--content" {...fadeUp(0.32)}>
                                    {HeroContent.subtitle}
                                </motion.p>

                                <motion.ul className="bt-hero__features" {...fadeUp(0.46)}>
                                    {HeroContent.features.map((feature, index) => (
                                        <li key={index}>
                                            <span className="bt-check" aria-hidden="true">
                                                <i className="fas fa-check" />
                                            </span>
                                            {feature}
                                        </li>
                                    ))}
                                </motion.ul>

                                <motion.div className="bt-hero__cta" {...fadeUp(0.60)}>
                                    <Link
                                        to={HeroContent.cta.link}
                                        className="thm-btn thm-btn--aso thm-btn--aso_yellow"
                                    >
                                        {HeroContent.cta.text}
                                    </Link>
                                    <Link to={HeroContent.secondaryCta.link} className="bt-btn-ghost">
                                        <span className="bt-play" aria-hidden="true">
                                            <i className="fas fa-arrow-right" />
                                        </span>
                                        {HeroContent.secondaryCta.text}
                                    </Link>
                                </motion.div>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <motion.div
                                className="hero-right_img pos-rel bt-hero__visual"
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <HeroVisual />
                            </motion.div>
                        </div>
                    </div>
                </div>

                <div className="banner-scroll-down active">
                    <AnchorLink href="#about" className="scrollspy-btn">
                        <span /><span /><span />
                    </AnchorLink>
                </div>
            </div>
        </section>
    );
};

export default Hero;